<template>
  <TsNuxtCard
    :data-testid="props.dataTestid + '-customer-address'"
    :append-class="
      twMerge(
        'data-test-customer-address bg-idle-white flex flex-col relative rounded-md px-3 py-3 h-full group',
        props.isSelectedForDelivery && 'border border-primary',
        props.appendClass
      )
    "
    noShadow
  >
    <div class="flex space-x-2">
      <TsTypography variant="sm" append-class="mb-2 flex gap-2">
        <TsIcon name="tdesign:location" class="shrink-0 size-5" />
        <span
          class="text-base"
          :data-testid="props.dataTestid + '-customer-address-formatted'"
          v-html="generateAddress(props.address.formatted)"
        ></span>
      </TsTypography>
    </div>

    <div v-if="layout === 'checkout'" class="flex items-center mt-auto justify-between">
      <TsButton
        v-if="isSelectedForDelivery"
        :data-testid="props.dataTestid + '-customer-address-unselect-for-delivery'"
        outlined
        :label="useTranslation('unselect', 'Deselecteer')"
        @click="emit('unselect', props.address.id)"
      />
      <TsButton
        v-else
        :data-testid="props.dataTestid + '-customer-address-select-for-delivery'"
        :label="selectText"
        @click="emit('select', props.address.id)"
      />
    </div>

    <TsRow append-class="justify-between items-center w-full m-0 pt-2">
      <TsImage
          v-if="props.checkoutAddressType === 'postnl'"
          src="/images/logos/postnl.webp"
          width="30"
          height="30"
          alt="postnl-address"
          :data-testid="props.dataTestid + '-customer-address-postnl-address-logo'"
          loading="lazy"
      />
      <div>
        <TsTag
          v-if="props.address.type === CustomerAddressType.PRIMARY && !props.hideDefaultTag"
          :label="byDefault"
          :data-testid="
            `${props.dataTestid}-customer-${props.checkoutAddressType}-address-default-tag`
          "
          :size="isMobile ? 'xs' : 'sm'"
          rounded
          variant="info"
        />
        <div v-else class="flex space-x-2 items-center pr-2">
          <template v-if="props.checkoutAddressType === 'home'">
            <TsTypography
              append-class="text-xs text-idle-black md:opacity-0 group-hover:opacity-100 transition-opacity duration-200 ease-in-out"
              weight="semibold"
              :data-testid="
                props.dataTestid + '-customer-address-set-as-default-text'
              "
              >{{
                useTranslation("AccountAddresses-defaultAddress", "Als standaard instellen")
              }}</TsTypography
            >
            <TsToggle
              size="md"
              :data-testid="
                props.dataTestid + '-customer-address-make-default-address'
              "
              @change="handleDefaultToggle"
              no-model-value
              append-class="md:opacity-0 group-hover:opacity-100 transition-opacity duration-200 ease-in-out"
            />
          </template>
        </div>
      </div>
      <div class="flex gap-x-0.5 items-center text-natural-grey">
        <TsIcon
          v-if="props.editable"
          name="tdesign:edit"
          @click="emit('edit')"
          :data-testid="props.dataTestid + '-customer-address-edit-icon'"
          class="size-5 cursor-pointer rounded transition-colors md:size-7"
        />
        <TsDivider v-if="props.editable && showDelete" layout="vertical" append-class="mx-1"/>
        <TsIcon
          v-if="showDelete"
          @click="emit('delete', props.address.id)"
          name="material-symbols:delete-outline"
          :size="isMobile ? '24' : '34'"
          :data-testid="props.dataTestid + '-customer-address-delete-icon'"
          class="cursor-pointer rounded transition-colors"
        />
      </div>
    </TsRow>
  </TsNuxtCard>
</template>

<script lang="ts" setup>
import {
  CustomerAddressType,
  type CustomerAddressItem,
} from "~/types/ecom/address.type";
import { useTranslation } from "~/composables/useTranslation";
import { twMerge } from "tailwind-merge";

const TsToggle = defineAsyncComponent(
    () => import('~/components/atoms/TsToggle.vue')
);

const TsNuxtCard = defineAsyncComponent(
    () => import('~/components/organisms/TsNuxtCard.vue')
)

const { isMobile } = useDevice();

type Props = {
  address: CustomerAddressItem;
  layout?: "profile" | "checkout";
  isSelectedForDelivery?: boolean;
  checkoutAddressType?: "postnl" | "home" | "shipping" | "billing";
  editable?: boolean;
  hideDefaultTag?: boolean;
  appendClass?: string;
  dataTestid: string;
};

const props = withDefaults(defineProps<Props>(), {
  layout: "profile",
  checkoutAddressType: "home",
  selectButtonLabel: "select",
  editable: true,
  appendClass: "",
  hideDefaultTag: false,
});

const emit = defineEmits<{
  makeDefault: [addressId: string];
  delete: [addressId: string];
  select: [addressId: string];
  unselect: [addressId: string];
  edit: any;
}>();

const showDelete = computed(() => props.address.type !== CustomerAddressType.PRIMARY);

function generateAddress(address?: Array<any>) {
  return address?.slice(0, address.length).join("<br>");
}

function handleDefaultToggle() {
  emit("makeDefault", props.address.id);
}

const byDefault = useTranslation("standard", "Standaard");
const selectText = useTranslation("select", "Select");
</script>
